import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { PurchaseOrderRecord } from '@/types/domain/po-management';
import { formatCurrency } from '@/lib/utils';
import { AlertTriangle, Scale, ShieldCheck, ExternalLink } from 'lucide-react';
import { ROUTES } from '@/constants/routes';

interface ThreeWayMatchPanelProps {
  po: PurchaseOrderRecord;
}

export const ThreeWayMatchPanel: React.FC<ThreeWayMatchPanelProps> = ({ po }) => {
  const navigate = useNavigate();
  const isMatched = po.threeWayMatchStatus === 'MATCHED';
  const hasMismatch = po.threeWayMatchStatus === 'QUANTITY_MISMATCH' || po.threeWayMatchStatus === 'PRICE_MISMATCH';

  return (
    <Card className="border-border rounded-2xl shadow-2xs font-heading">
      <CardHeader className="pb-3 border-b border-border/60">
        <CardTitle className="font-heading text-xs font-bold text-foreground uppercase tracking-wider flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Scale className="h-4 w-4 text-purple-600" />
            <span>3-Way Financial Matching Audit (PO vs GRN vs Invoice)</span>
          </div>

          <span
            className={`text-[10px] font-extrabold uppercase px-2.5 py-0.5 rounded-full ${
              isMatched
                ? 'bg-emerald-500/10 text-emerald-700 border border-emerald-500/20'
                : hasMismatch
                ? 'bg-rose-500/10 text-rose-700 border border-rose-500/20'
                : 'bg-amber-500/10 text-amber-700 border border-amber-500/20'
            }`}
          >
            {po.threeWayMatchStatus.replace(/_/g, ' ')}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-4 space-y-4">
        {/* Match Comparison Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 rounded-xl border border-primary/20 bg-primary/5 space-y-1">
            <span className="text-[10px] font-extrabold text-primary uppercase block">1. PO Authorized Total</span>
            <span className="font-mono text-lg font-extrabold text-foreground block">
              {formatCurrency(po.totalValue, po.currency)}
            </span>
            <span className="text-[10px] text-foreground-muted block font-mono">PO #: {po.poNumber}</span>
          </div>

          <div
            onClick={() => {
              if (po.grnIds.length > 0) navigate(ROUTES.GRN.DETAILS(po.grnIds[0]!));
            }}
            className={`p-4 rounded-xl border border-emerald-500/20 bg-emerald-500/5 space-y-1 ${
              po.grnIds.length > 0 ? 'cursor-pointer hover:bg-emerald-500/10 transition-colors' : ''
            }`}
          >
            <span className="text-[10px] font-extrabold text-emerald-700 uppercase flex items-center justify-between">
              <span>2. GRN Goods Received Value</span>
              {po.grnIds.length > 0 && <ExternalLink className="h-3 w-3" />}
            </span>
            <span className="font-mono text-lg font-extrabold text-emerald-600 block">
              {po.grnIds.length > 0 ? formatCurrency(po.totalValue, po.currency) : '₹0 (Pending)'}
            </span>
            <span className="text-[10px] text-emerald-700 font-bold block font-mono">
              GRN #: {po.grnIds[0] || 'Not Created'}
            </span>
          </div>

          <div
            onClick={() => {
              if (po.invoiceIds.length > 0) navigate(ROUTES.INVOICES.DETAILS(po.invoiceIds[0]!));
            }}
            className={`p-4 rounded-xl border border-purple-500/20 bg-purple-500/5 space-y-1 ${
              po.invoiceIds.length > 0 ? 'cursor-pointer hover:bg-purple-500/10 transition-colors' : ''
            }`}
          >
            <span className="text-[10px] font-extrabold text-purple-700 uppercase flex items-center justify-between">
              <span>3. Invoice Billed Total</span>
              {po.invoiceIds.length > 0 && <ExternalLink className="h-3 w-3" />}
            </span>
            <span className="font-mono text-lg font-extrabold text-purple-600 block">
              {po.invoiceIds.length > 0 ? formatCurrency(po.totalValue, po.currency) : '₹0 (Pending)'}
            </span>
            <span className="text-[10px] text-purple-700 font-bold block font-mono">
              Invoice #: {po.invoiceIds[0] || 'Not Received'}
            </span>
          </div>
        </div>

        {/* Audit Callout */}
        <div
          className={`p-3.5 rounded-xl border flex items-center justify-between text-xs ${
            isMatched
              ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-800 font-bold'
              : hasMismatch
              ? 'border-rose-500/30 bg-rose-500/10 text-rose-900 font-bold'
              : 'border-amber-500/30 bg-amber-500/10 text-amber-900 font-bold'
          }`}
        >
          <div className="flex items-center gap-2">
            {isMatched ? (
              <ShieldCheck className="h-5 w-5 text-emerald-600" />
            ) : (
              <AlertTriangle className="h-5 w-5 text-rose-600" />
            )}
            <span>
              {isMatched
                ? '3-Way Match Verified: Purchase Order, GRN Receipts, and Vendor Invoice line items match 100%.'
                : hasMismatch
                ? '3-Way Match Exception: Quantity or price variance detected between PO ordered items and delivered GRN quantity.'
                : '3-Way Match Pending: Awaiting complete GRN receipt and vendor invoice submission.'}
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
